import { isApiError } from '@/helpers';
import { messages } from '@/locales/fr';
import { IODUserForm } from '@/model';
import { IAuthUserData } from '@homeserve/od-typings/api';
import { Nullable, Optional } from '@homeserve/od-typings/utils';
import { userHelper } from '@homeserve/vue-api-plugin';
import { authHelper } from '@homeserve/vue-auth-plugin';
import { parsePhoneNumberFromString } from 'libphonenumber-js/min';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import moment from 'moment';
import { ErrorBag } from 'vee-validate';
import { Store } from 'vuex';

export function dateFormat(value?: string, format = 'L') {
  if (!value) {
    return;
  }

  return moment(value).format(format);
}

export function deepDiff<T extends object, TResult = Partial<T>>(object: T, base: T): TResult {
  return Object.keys(object).reduce<TResult>(
    (accumulator, key) => {
      const value = object[key];
      const baseValue = base[key];

      if (!isEqual(value, baseValue)) {
        accumulator[key] = isObject(value) && isObject(baseValue) ? deepDiff(value, baseValue) : value;
      }

      return accumulator;
    },
    {} as TResult,
  );
}

export function previewFile(file: File) {
  const url = window.URL.createObjectURL(file);

  const link = document.createElement('a');
  link.href = url;
  link.download = file.name;
  link.click();

  window.URL.revokeObjectURL(url);
}

export async function createUserInfo(payload: IODUserForm, isAuthenticated = false, store: Store<any>) {
  const phoneNumber = parsePhoneNumberFromString(payload.phone, 'FR');

  if (phoneNumber) {
    payload.phone = phoneNumber.number.toString();
  }

  let error: any;
  let result: Optional<Nullable<IAuthUserData>> = null;

  if (isAuthenticated) {
    const response = await userHelper.updateUserInfo(store, payload);

    if (response && response.error) {
      error = response.error;
    }
  } else {
    ({ error, result } = await userHelper.createUserInfo(store, payload));
  }

  if (error) {
    throw error;
  }

  if (!result) {
    return;
  }

  if (!isAuthenticated) {
    await authHelper.setAuthUserData(store, result);
  }
}

export function handleUserFormError(error: any, errorBag: ErrorBag) {
  if (isApiError(error) && error.response.status === 409 && error.response.data.message === 'user already exists') {
    errorBag.add({
      field: 'email',
      scope: 'userForm',
      msg: messages.onboarding.errors.emailAlreadyTaken,
    });
    errorBag.add({
      field: 'onboarding',
      msg: messages.common.formErrors,
    });
  } else {
    errorBag.add({
      field: 'onboarding',
      msg: messages.errors.unexpected,
    });
  }
}
